import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Chat } from 'src/model/chat';
import type { ChatMessage } from 'src/model/ChatMessage';
import type { ChatMessageRequest } from 'src/model/ChatMessageRequest';
import MessageType from 'src/model/MessageType';
import type Client from 'src/model/User';
import { UserType } from 'src/model/UserType';
import { api } from 'src/services/api';
import { websocketService } from 'src/services/websocket';

interface ChatComponentProps {
  user: Client;
}

export const ChatComponent: React.FC<ChatComponentProps> = ({ user }) => {
  const [buyerChats, setBuyerChats] = useState<Chat[]>([]);
  const [sellerChats, setSellerChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const chatIdSelected = useRef<string>('');

  const orderedMessages = [...messages].sort(
    (a, b) =>
      new Date(a.messageDate).getTime() - new Date(b.messageDate).getTime()
  );

  const getChats = async (): Promise<Chat[]> => {
    try {
      if (user.type === UserType.SELLER || user.type === UserType.ADMIN) {
        const [buyerResponse, sellerResponse] = await Promise.all([
          api.get<Chat[]>('/chat/buyer/'),
          api.get<Chat[]>('/chat/seller/'),
        ]);
        const buyer = buyerResponse.data;
        const seller = sellerResponse.data;
        setBuyerChats(buyer);
        setSellerChats(seller);
        return [...buyer, ...seller];
      } else {
        const buyerResponse = await api.get<Chat[]>('/chat/buyer/');
        const buyer = buyerResponse.data;
        setBuyerChats(buyer);
        return buyer;
      }
    } catch (error) {
      console.error('Erro ao buscar os chats:', error);
      return [];
    }
  };

  const getAllChats = (): Chat[] => {
    return [...buyerChats, ...sellerChats];
  };

  const getLastMessageDate = (chat: Chat): Date => {
    const allDates = [];
    if (chat.chatMessage?.length) {
      for (const msg of chat.chatMessage) {
        if (msg.messageDate) {
          allDates.push(new Date(msg.messageDate));
        }
      }
    }
    if (allDates.length > 0) {
      return new Date(Math.max(...allDates.map((d) => d.getTime())));
    }
    return new Date(chat.createAt);
  };

  const handleSelectChat = async (chatId: string): Promise<void> => {
    const allChats = getAllChats();
    const selected = allChats.find((chat) => chat.id === chatId);
    if (!selected) return;

    chatIdSelected.current = chatId;

    if (!selected.chatMessage) {
      setMessages([]);
      setSelectedChat(chatId);
      return;
    }

    const unreadMessages = selected.chatMessage.filter(
      (msg) => !msg.read && msg.user !== user.id
    );

    if (unreadMessages.length > 0) {
      await Promise.all(
        unreadMessages.map((msg) => api.get(`/chat/read-messages/${msg.id}`))
      );
    }

    const updatedMessages = selected.chatMessage.map((msg) =>
      msg.user !== user.id ? { ...msg, read: true } : msg
    );

    setMessages(updatedMessages);
    setSelectedChat(chatId);
  };

  const handleBackToChats = () => {
    setSelectedChat(null);
    setMessages([]);
  };

  const handleSendMessage = async (
    chatId: string,
    recipientId: string
  ): Promise<void> => {
    if (newMessage.trim() === '') return;

    const payload: ChatMessageRequest = {
      chat: chatId,
      message: newMessage,
      messageType:
        user.type === UserType.SELLER ? MessageType.SELLER : MessageType.CLIENT,
      recipient: recipientId,
    };

    try {
      await api.post(`/chat/createMessage`, payload);

      const newChatMessage: ChatMessage = {
        chat: chatId,
        message: newMessage,
        messageType: payload.messageType,
        user: user.id,
        read: false,
        messageDate: new Date(),
      };

      setMessages((prev) => [...prev, newChatMessage]);
      setNewMessage('');
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const getName = (id: string): string => {
    const allChats = getAllChats();
    const chat = allChats.find((chat) => chat.id === id);
    if (!chat) return '';

    return chat.userBuyer.userId !== user.id
      ? chat.userBuyer.name
      : chat.userSeller.name;
  };
  const getSellerId = (idChat: string): string => {
    const allChats = getAllChats();
    let id;
    if (allChats) {
      for (const chat of allChats) {
        if (chat.id == idChat) {
          if (chat.userBuyer.userId != user.id) {
            id = chat.userBuyer.userId;
          } else if (chat.userSeller.userId != user.id) {
            id = chat.userSeller.userId;
          }
        }
      }
    }
    return id || '';
  };

  const refreshCurrentChat = async () => {
    const id = chatIdSelected.current;
    if (!id) return;

    const allChats = await getChats();
    const selected = allChats.find((c) => c.id === id);
    if (selected && selected.chatMessage) {
      const updatedMessages = selected.chatMessage.map((msg) =>
        msg.user !== user.id ? { ...msg, read: true } : msg
      );
      setMessages(updatedMessages);
    }
  };

  const filteredChats = (chats: Chat[]) => {
    return chats.filter(
      (chat) =>
        chat.userSeller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.chatMessage?.some((msg) =>
          msg.message.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
  };

  useEffect(() => {
    getChats();
  }, []);

  // const websocket = () => {
  //   if (!user) return;
  //   websocketService.connect(user.id);

  //   const handleNewMessage = async (data: any) => {
  //     console.log(data);
  //     if (data.eventType === 'chat:new_message') {
  //       refreshCurrentChat();
  //     }
  //   };
  //   websocketService.addListener(handleNewMessage);
  // };

  // useEffect(() => {
  //   return websocket();
  // }, []);

  useEffect(() => {
    if (!user?.id) return;

    const handleNewMessage = async (data: any) => {
      if (data.eventType === 'chat:new_message') {
        await refreshCurrentChat();
      }
    };

    websocketService.connect(user.id);
    websocketService.addListener(handleNewMessage);

    return () => {
      websocketService.removeListener(handleNewMessage);
      websocketService.disconnect();
    };
  }, [user?.id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [messages]);

  const renderChatItem = (chat: Chat, type: 'buyer' | 'seller') => {
    const unreadCount =
      chat.chatMessage?.filter((msg) => !msg.read && msg.user !== user.id)
        .length || 0;
    const lastMessage = chat.chatMessage?.[chat.chatMessage.length - 1];
    const isSelected = selectedChat === chat.id;

    return (
      <div
        key={chat.id}
        onClick={() => handleSelectChat(chat.id)}
        className={`group relative p-4 cursor-pointer transition-all duration-300 hover:bg-gradient-to-r ${
          type === 'buyer'
            ? 'hover:from-red-50 hover:to-red-50'
            : 'hover:from-emerald-50 hover:to-green-50'
        } ${
          isSelected
            ? `bg-gradient-to-r border-l-4 ${
                type === 'buyer'
                  ? 'from-red-100 to-red-100 border-l-red-500'
                  : 'from-emerald-100 to-green-100 border-l-emerald-500'
              }`
            : 'border-l-4 border-l-transparent'
        }`}
      >
        <div className="flex items-start space-x-3">
          <div
            className={`relative flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br ${
              type === 'buyer'
                ? 'from-red-500 to-red-600'
                : 'from-emerald-500 to-green-600'
            } flex items-center justify-center text-white font-bold text-lg shadow-lg`}
          >
            {chat.userSeller.name.charAt(0).toUpperCase()}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-900 truncate text-base">
                {getName(chat.id)}
              </h3>
              {unreadCount > 0 && (
                <div
                  className={`px-2 py-1 rounded-full text-xs font-bold text-white ${
                    type === 'buyer' ? 'bg-red-500' : 'bg-emerald-500'
                  } animate-pulse min-w-[20px] text-center`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 truncate mb-1">
              {lastMessage?.message || 'Nenhuma mensagem ainda'}
            </p>

            <div className="flex items-center justify-between">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  type === 'buyer'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-emerald-100 text-emerald-800'
                }`}
              >
                {type === 'buyer' ? 'Vendedor' : 'Comprador'}
              </span>
              <time className="text-xs text-gray-400">
                {dayjs(getLastMessageDate(chat)).format('HH:mm')}
              </time>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex w-full">
      <div className="hidden lg:block w-full max-w-md lg:w-96 bg-white/80 backdrop-blur-sm border-r border-gray-200/50 shadow-xl">
        <div className="p-4 border-b border-gray-200/50">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar conversas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 text-sm"
            />
            <svg
              className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="overflow-y-auto h-[calc(100vh-100px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {filteredChats(buyerChats).length > 0 && (
            <div className="space-y-1">
              {filteredChats(buyerChats)
                .sort(
                  (a, b) =>
                    getLastMessageDate(b).getTime() -
                    getLastMessageDate(a).getTime()
                )
                .map((chat) => renderChatItem(chat, 'buyer'))}
            </div>
          )}

          {filteredChats(sellerChats).length > 0 && (
            <div className="space-y-1 mt-2">
              {filteredChats(sellerChats)
                .sort(
                  (a, b) =>
                    getLastMessageDate(b).getTime() -
                    getLastMessageDate(a).getTime()
                )
                .map((chat) => renderChatItem(chat, 'seller'))}
            </div>
          )}

          {filteredChats([...buyerChats, ...sellerChats]).length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-center text-sm">
                {searchTerm
                  ? 'Nenhuma conversa encontrada'
                  : 'Nenhuma conversa ainda'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="lg:hidden w-full min-h-screen">
        {!selectedChat ? (
          <div className="h-full bg-white w-full">
            <div className="bg-white border-b border-gray-200 px-2 py-4 w-full">
              <h1 className="text-xl font-bold text-gray-900">Conversas</h1>
            </div>

            <div className="px-4 py-3 border-b border-gray-200/50 w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar conversas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 text-base"
                />
                <svg
                  className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <div className="overflow-y-auto h-[calc(100vh-140px)]">
              {filteredChats(buyerChats).length > 0 && (
                <div className="space-y-1">
                  {filteredChats(buyerChats)
                    .sort(
                      (a, b) =>
                        getLastMessageDate(b).getTime() -
                        getLastMessageDate(a).getTime()
                    )
                    .map((chat) => renderChatItem(chat, 'buyer'))}
                </div>
              )}

              {filteredChats(sellerChats).length > 0 && (
                <div className="space-y-1 mt-2">
                  {filteredChats(sellerChats)
                    .sort(
                      (a, b) =>
                        getLastMessageDate(b).getTime() -
                        getLastMessageDate(a).getTime()
                    )
                    .map((chat) => renderChatItem(chat, 'seller'))}
                </div>
              )}

              {filteredChats([...buyerChats, ...sellerChats]).length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-center text-base">
                    {searchTerm
                      ? 'Nenhuma conversa encontrada'
                      : 'Nenhuma conversa ainda'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col bg-white w-full">
            <div className="bg-white/95 backdrop-blur-sm border-b border-gray-200/50 px-4 py-4 shadow-sm w-full">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBackToChats}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors -ml-1"
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {getName(selectedChat).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-gray-900 text-lg truncate">
                    {getName(selectedChat)}
                  </h2>
                  <p className="text-sm text-gray-500">Online</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/30 to-white px-4 py-3 w-full">
              <div className="space-y-4">
                {orderedMessages.map((msg, index) => {
                  const isMe = msg.user === user.id;
                  const showAvatar =
                    index === 0 ||
                    orderedMessages[index - 1]?.user !== msg.user;

                  return (
                    <div
                      key={index}
                      className={`flex items-end space-x-2 ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}
                    >
                      {!isMe && showAvatar && (
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {getName(selectedChat).charAt(0).toUpperCase()}
                        </div>
                      )}
                      {!isMe && !showAvatar && <div className="w-8"></div>}

                      <div className={`max-w-[75%] ${isMe ? 'ml-auto' : ''}`}>
                        <div
                          className={`px-4 py-3 rounded-2xl shadow-sm ${
                            isMe
                              ? 'bg-gradient-to-br from-red-500 to-red-600 text-white rounded-br-md'
                              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {msg.message}
                          </p>
                        </div>
                        <div
                          className={`flex items-center mt-1 space-x-2 ${isMe ? 'justify-end' : ''}`}
                        >
                          <time className="text-xs text-gray-500">
                            {dayjs(msg.messageDate).format('HH:mm')}
                          </time>
                          {isMe && (
                            <div className="flex space-x-1">
                              <div className="w-1 h-1 bg-red-400 rounded-full"></div>
                              <div className="w-1 h-1 bg-red-400 rounded-full"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {isTyping && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {getName(selectedChat).charAt(0).toUpperCase()}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0.1s' }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm border-t border-gray-200/50 px-4 py-3 w-full">
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  <textarea
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(
                          selectedChat,
                          getSellerId(selectedChat)
                        );
                      }
                    }}
                    className="w-full px-4 py-3 pr-12 bg-gray-50/80 border border-gray-200/50 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 transition-all duration-200 min-h-[52px] max-h-32 text-base"
                    rows={1}
                  />
                  <div className="absolute right-3 bottom-3">
                    <button
                      onClick={() =>
                        handleSendMessage(
                          selectedChat,
                          getSellerId(selectedChat)
                        )
                      }
                      disabled={!newMessage.trim()}
                      className="p-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed shadow-lg"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedChat ? (
        <div className="hidden lg:flex flex-1 flex-col bg-white">
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200/50 px-6 py-4 shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {getName(selectedChat).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-gray-900 text-lg">
                  {getName(selectedChat)}
                </h2>
                <p className="text-sm text-gray-500">Online</p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/30 to-white p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <div className="max-w-4xl mx-auto space-y-6">
              {orderedMessages.map((msg, index) => {
                const isMe = msg.user === user.id;
                const showAvatar =
                  index === 0 || orderedMessages[index - 1]?.user !== msg.user;

                return (
                  <div
                    key={index}
                    className={`flex items-end space-x-3 ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}
                  >
                    {!isMe && showAvatar && (
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {getName(selectedChat).charAt(0).toUpperCase()}
                      </div>
                    )}
                    {!isMe && !showAvatar && <div className="w-8"></div>}

                    <div
                      className={`max-w-xs lg:max-w-md ${isMe ? 'ml-auto' : ''}`}
                    >
                      <div
                        className={`px-4 py-3 rounded-2xl shadow-sm ${
                          isMe
                            ? 'bg-gradient-to-br from-red-500 to-red-600 text-white rounded-br-md'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {msg.message}
                        </p>
                      </div>
                      <div
                        className={`flex items-center mt-1 space-x-2 ${isMe ? 'justify-end' : ''}`}
                      >
                        <time className="text-xs text-gray-500">
                          {dayjs(msg.messageDate).format('HH:mm')}
                        </time>
                        {isMe && (
                          <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-red-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-red-400 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {isTyping && (
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                    {getName(selectedChat).charAt(0).toUpperCase()}
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.1s' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-gray-200/50 p-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-end space-x-4">
                <div className="flex-1 relative">
                  <textarea
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(
                          selectedChat,
                          getSellerId(selectedChat)
                        );
                      }
                    }}
                    className="w-full px-4 py-3 pr-12 bg-gray-50/80 border border-gray-200/50 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 transition-all duration-200 min-h-[52px] max-h-32"
                    rows={1}
                  />
                  <div className="absolute right-3 bottom-3">
                    <button
                      onClick={() =>
                        handleSendMessage(
                          selectedChat,
                          getSellerId(selectedChat)
                        )
                      }
                      disabled={!newMessage.trim()}
                      className="p-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed shadow-lg"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="relative mb-8">
              <div className="w-32 h-32 bg-gradient-to-br from-red-400 via-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                <svg
                  className="w-16 h-16 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Chat</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Selecione uma conversa da lista ao lado para começar a trocar
              mensagens
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
